/**
 * AlertsPage — V2 real-time nurse call alerts page.
 *
 * 4 tabs: Emergency (real-time), Batteries (60s poll), Communication Lost (120s poll), Other (60s poll).
 * Sound notifications for new emergency alarms. Station Mode (fullscreen).
 * NotificationContext integration for real-time emergency alerts.
 *
 * Props: fullscreen — boolean for station mode
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useSound from 'use-sound';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import { useNotifications } from '../../../contexts/NotificationContext.jsx';
import {
    getSession, getCampusId, getCampusList, updateSessionCampus,
} from '../../../services/session.js';
import { iAmHigherAuthority, sortFunc, campusToGuid, userToGuid, setSoundsMute } from '../../../libs.jsx';
import {
    fetchMaintenanceAlerts, fetchLostDevices, clearMaintenanceAlert,
} from './api/alertsApi.js';

import EmergencyTable from './components/EmergencyTable.jsx';
import MaintenanceTable from './components/MaintenanceTable.jsx';
import LostDevicesTable from './components/LostDevicesTable.jsx';
import AlertSyncIndicator from './components/AlertSyncIndicator.jsx';
import StationModeInfoTip from './components/StationModeInfoTip.jsx';
import emergSound from '../../../sound/emergency_tone.mp3';

import './alerts-page.css';

const AlertsPage = ({ fullscreen: fullscreenProp }) => {
    // ======================================================================
    // SESSION
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);
    const tzOffset = campusConfig?.tz_offset;
    const isCloud = Boolean(campusConfig?.is_cloud);
    const myRoleWeight = session?.my_role_weight || 0;
    const showAllByRole = iAmHigherAuthority(10, myRoleWeight);
    const myGroupGuids = session?.groupGuids || [];

    // ======================================================================
    // NOTIFICATION CONTEXT (real-time emergency alerts)
    // ======================================================================
    const {
        activeAlarms, isConnected, connectionError, isDataStale,
        registerAlertViewer, unregisterAlertViewer,
    } = useNotifications();

    useEffect(() => {
        registerAlertViewer();
        return () => unregisterAlertViewer();
    }, [registerAlertViewer, unregisterAlertViewer]);

    // ======================================================================
    // STATE
    // ======================================================================
    const [activeTab, setActiveTab] = useState('emergency');
    const [fullscreen, setFullscreen] = useState(Boolean(fullscreenProp));
    const [lowBatData, setLowBatData] = useState([]);
    const [otherData, setOtherData] = useState([]);
    const [lostData, setLostData] = useState([]);
    const [clearing, setClearing] = useState(null);

    // Sort state
    const [emergSort, setEmergSort] = useState({ col: 'EventDateTime', dir: 'asc' });
    const [maintSort, setMaintSort] = useState({ col: 'EventDateTime', dir: 'asc' });
    const [lostSort, setLostSort] = useState({ col: 'last_active', dir: 'asc' });

    // Sound
    const [emergencySound] = useSound(emergSound, { volume: 0.5 });
    const [soundOff, setSoundOff] = useState(session?.soundsOff || false);
    const alertKeyListRef = useRef([]);

    // Toast
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 6000);
    }, []);
    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // ======================================================================
    // FILTER EMERGENCY ALERTS (role-based + fullscreen)
    // ======================================================================
    const filteredEmergency = useMemo(() => {
        let filtered = showAllByRole
            ? [...(activeAlarms || [])]
            : (activeAlarms || []).filter((alarm) => {
                const alarmGroupIds = alarm.GroupIds ?? [];
                return alarmGroupIds.some((gid) => myGroupGuids?.includes(gid));
            });
        if (fullscreen) {
            filtered = filtered.filter((alarm) => !alarm?.AckDateTime);
        }
        return filtered;
    }, [activeAlarms, showAllByRole, myGroupGuids, fullscreen]);

    // ======================================================================
    // SOUND — play for new alarm IDs
    // ======================================================================
    useEffect(() => {
        let playSound = false;
        const currentKeys = alertKeyListRef.current;
        filteredEmergency.forEach((alarm) => {
            if (!currentKeys.includes(alarm.AlarmId)) {
                if (!soundOff) playSound = true;
                currentKeys.push(alarm.AlarmId);
            }
        });
        alertKeyListRef.current = currentKeys;
        if (playSound) emergencySound();
    }, [filteredEmergency, soundOff, emergencySound]);

    // ======================================================================
    // SORTED DATA
    // ======================================================================
    const sortedEmergency = useMemo(() => {
        const sorted = [...filteredEmergency];
        sorted.sort((a, b) => sortFunc(a, b, emergSort.col, emergSort.dir));
        return sorted;
    }, [filteredEmergency, emergSort]);

    const sortedLowBat = useMemo(() => {
        const sorted = [...lowBatData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [lowBatData, maintSort]);

    const sortedOther = useMemo(() => {
        const sorted = [...otherData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [otherData, maintSort]);

    const sortedLost = useMemo(() => {
        const sorted = [...lostData];
        sorted.sort((a, b) => sortFunc(a, b, lostSort.col, lostSort.dir));
        return sorted;
    }, [lostData, lostSort]);

    // ======================================================================
    // POLLING — Maintenance (60s) + Lost Devices (120s)
    // ======================================================================
    const loadMaintenance = useCallback(async () => {
        if (!campusId || !session) return;
        try {
            const campusGuid = campusToGuid(session, campusId);
            const userGuid = userToGuid(session, session.user);
            const data = await fetchMaintenanceAlerts(campusGuid, userGuid, session.migrated, fullscreen ? 'station' : 'standard', {
                hybrid: session.hybrid, legacy: session.legacy, new: session.new,
            });
            const tmpLow = [];
            const tmpOther = [];
            (data || []).forEach((a) => {
                const subType = (a.AlertSubType || '').toLowerCase();
                if (['low battery', 'critbattery', 'crit battery'].includes(subType)) {
                    tmpLow.push(a);
                } else if (!['communication lost'].includes(subType)) {
                    tmpOther.push(a);
                }
            });
            setLowBatData(tmpLow);
            setOtherData(tmpOther);
        } catch (err) {
            console.error('[AlertsPage] loadMaintenance error:', err);
        }
    }, [campusId, session, fullscreen]);

    const loadLostDevices = useCallback(async () => {
        if (!campusId) return;
        try {
            const data = await fetchLostDevices(campusId);
            setLostData(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('[AlertsPage] loadLostDevices error:', err);
        }
    }, [campusId]);

    useEffect(() => { loadMaintenance(); }, [loadMaintenance]);
    useEffect(() => { loadLostDevices(); }, [loadLostDevices]);

    useEffect(() => {
        const maintTimer = setInterval(loadMaintenance, 60000);
        const lostTimer = setInterval(loadLostDevices, 120000);
        return () => { clearInterval(maintTimer); clearInterval(lostTimer); };
    }, [loadMaintenance, loadLostDevices]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        const cfg = session?.campusData?.[val.toString()];
        // Cloud/on-premise redirect
        if (cfg?.is_cloud && !isCloud) {
            updateSessionCampus(val);
            window.location.href = '/emergency';
            return;
        }
        if (!cfg?.is_cloud && isCloud) {
            updateSessionCampus(val);
            window.location.href = '/alerts';
            return;
        }
        setCampusId(val);
        updateSessionCampus(val);
    }, [session, isCloud]);

    const handleSoundToggle = useCallback((e) => {
        const muted = e.target.checked;
        setSoundOff(muted);
        if (session) setSoundsMute(session, muted);
    }, [session]);

    const handleClearMaint = useCallback(async (alarmId) => {
        setClearing(alarmId);
        const campusGuid = campusToGuid(session, campusId);
        const result = await clearMaintenanceAlert(campusGuid, alarmId, session?.username);
        setClearing(null);
        if (result[0]) {
            showToast('success', 'Maintenance alarm cleared');
            loadMaintenance();
        } else {
            showToast('error', result[1] || 'Failed to clear alarm');
        }
    }, [session, campusId, showToast, loadMaintenance]);

    const handleEmergSort = useCallback((col) => {
        setEmergSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleMaintSort = useCallback((col) => {
        setMaintSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleLostSort = useCallback((col) => {
        setLostSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            {!fullscreen ? <NavigationV2 /> : null}
            <div className={fullscreen ? '' : 'nav2-main'}>
                <div className={`na-page${fullscreen ? ' na-page--fullscreen' : ''}`}>
                    {/* Toast */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`} role="status" aria-live="polite">
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="dp-toast__close">{'\u2715'}</button>
                        </div>
                    ) : null}

                    {/* Header */}
                    <div className="na-header nav2-header-offset">
                        <div className="na-header__left">
                            <h1 className="na-header__title">Active Alerts</h1>
                            <AlertSyncIndicator isConnected={isConnected} isDataStale={isDataStale} connectionError={connectionError} />
                        </div>
                        <div className="na-header__right">
                            <label className="na-sound-toggle">
                                <input type="checkbox" checked={soundOff} onChange={handleSoundToggle} />
                                <span>Mute Sound</span>
                            </label>
                            {!fullscreen ? (
                                <>
                                    <StationModeInfoTip>
                                        <button className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(true)}>
                                            <i className="fe fe-maximize" style={{ marginRight: 4 }} />Station Mode
                                        </button>
                                    </StationModeInfoTip>
                                    <div className="dp-campus-row">
                                        <label htmlFor="na-campus" className="dp-campus-label">Campus:</label>
                                        {campusList.length > 1 ? (
                                            <select id="na-campus" className="dp-campus-select" value={campusId} onChange={handleCampusChange}>
                                                {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                            </select>
                                        ) : (
                                            <span className="dp-campus-name">{campusConfig?.name || ''}</span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <button className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(false)}>
                                    <i className="fe fe-minimize" style={{ marginRight: 4 }} />Exit Station Mode
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tab Bar */}
                    <div className="na-tab-bar">
                        <button className={`na-tab${activeTab === 'emergency' ? ' is-active' : ''}`} onClick={() => setActiveTab('emergency')}>
                            Emergency
                            {filteredEmergency.length > 0 ? <span className="na-badge na-badge--danger">{filteredEmergency.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'batteries' ? ' is-active' : ''}`} onClick={() => setActiveTab('batteries')}>
                            Batteries
                            {lowBatData.length > 0 ? <span className="na-badge na-badge--info">{lowBatData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'lost' ? ' is-active' : ''}`} onClick={() => setActiveTab('lost')}>
                            Communication Lost
                            {lostData.length > 0 ? <span className="na-badge na-badge--info">{lostData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'other' ? ' is-active' : ''}`} onClick={() => setActiveTab('other')}>
                            Other
                            {otherData.length > 0 ? <span className="na-badge na-badge--info">{otherData.length}</span> : null}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="na-content">
                        {activeTab === 'emergency' ? (
                            <EmergencyTable data={sortedEmergency} sortColumn={emergSort.col} sortOrder={emergSort.dir} onSort={handleEmergSort} tzOffset={tzOffset} />
                        ) : null}

                        {activeTab === 'batteries' ? (
                            <MaintenanceTable data={sortedLowBat} onClear={handleClearMaint} clearing={clearing} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}

                        {activeTab === 'lost' ? (
                            <LostDevicesTable data={sortedLost} sortColumn={lostSort.col} sortOrder={lostSort.dir} onSort={handleLostSort} />
                        ) : null}

                        {activeTab === 'other' ? (
                            <MaintenanceTable data={sortedOther} onClear={handleClearMaint} clearing={clearing} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AlertsPage;
